<template>
    <div class="upgradePlan">
        <template v-if="showPreviewPlan">
            <change-plan-new
                ref="changePlanNew"
                :defaultPlan="plan"
                :defaultType="type"
                :hasCard="hasCard"
                :price="price"
                :hasPlanDowngrade="hasPlanDowngrade"
                :resetPlan="false"
                :newBilling="true"
                :currentPlan="currentPlan"
                :target="target"
                :defaultPlanName="getNameDefaultPlan"
                @closed="close"
                @handle-change-data="handleChangeData"
            />
        </template>
        <template v-else>
            <form-message v-if="form.errors.has('stripe')" :message="form.errors.get('stripe')" />
            <form-message
                v-else-if="form.errors.any()"
                message="Something went wrong, please try again."
            />

            <div class="upgradePlan__title">
                {{ modalTitle }}
            </div>

            <div class="upgradePlanSlider">
                <div class="upgradePlanSlider__top">
                    <div class="upgradePlanSlider__line">
                        <div
                            v-for="item in stepLine"
                            :style="{
                                width: `${100 / stepLine.length}%`,
                            }"
                        />
                    </div>
                    <Slider
                        v-bind:key="target"
                        v-model="value"
                        :format="formatChange"
                        :min="min"
                        :max="max"
                        :step="step"
                        :show-tooltip="'always'"
                        :tooltip-position="'top'"
                        :lazy="false"
                        class="slider-red"
                    />
                </div>
                <div class="upgradePlanSlider__bottom">
                    <span>{{ firstPlanCredits }}</span>
                    <span>{{ lastPlanCredits }} +</span>
                </div>
            </div>

            <div class="upgradePlanToggle">
                <toggle-buttons
                    :buttons="buttons"
                    :default="getNameDefaultPlan === 'annual' ? 1 : 0"
                    @toggle-buttons="toggleButtons"
                />
            </div>

            <div class="upgradePlan__info">
                <span>Get 2 months free!</span>
                <img
                    :src="`${$window.Config.cdn_url}/images/app/svg/arrow-top-ellips.svg`"
                    alt=""
                />
            </div>

            <div class="upgradePlan__content">
                <div class="upgradePlan__choose">
                    <template v-if="target === 'monthly'">
                        <upgrade-plan-item
                            v-if="!isSelfUpgrade && isLastOption"
                            v-bind:key="'custom-plan-monthly'"
                            :type="'custom-monthly'"
                            :plan="currentPlan.id"
                            :period="'monthly'"
                            :currentStep="currentStep"
                            :planList="planList"
                            :planCredit="getDefaultPlanCredit"
                            :defaultPlanName="getNameDefaultPlan"
                            :defaultTarget="defaultTarget"
                            :currentTarget="target"
                            :lastOption="true"
                            @handle-change-data="handleChangeData"
                            @close="close"
                        />

                        <upgrade-plan-item
                            v-if="isSelfUpgrade || !isLastOption"
                            v-bind:key="'pro-plan-monthly'"
                            :type="'pro-plan-monthly'"
                            :plan="currentPlan.id"
                            :period="'monthly'"
                            :currentStep="currentStep"
                            :planList="planList"
                            :planCredit="getDefaultPlanCredit"
                            :defaultPlanName="getNameDefaultPlan"
                            :defaultTarget="defaultTarget"
                            :currentTarget="target"
                            @handle-change-data="handleChangeData"
                            @close="close"
                        />
                        <upgrade-plan-item
                            v-if="isSelfUpgrade || !isLastOption"
                            v-bind:key="'custom-monthly'"
                            :type="'custom-monthly'"
                            :plan="'custom-monthly'"
                            :period="'monthly'"
                            :currentStep="currentStep"
                            :planList="planList"
                            @handle-change-data="handleChangeData"
                            @close="close"
                        />
                    </template>
                    <template v-else>
                        <upgrade-plan-item
                            v-if="!isSelfUpgrade && isLastOption"
                            v-bind:key="'custom-plan-annual'"
                            :type="'custom-annual'"
                            :plan="currentPlan.id"
                            :period="'annual'"
                            :currentStep="currentStep"
                            :planList="planList"
                            :planCredit="getDefaultPlanCredit"
                            :defaultTarget="defaultTarget"
                            :currentTarget="target"
                            :defaultPlanName="getNameDefaultPlan"
                            :lastOption="true"
                            @handle-change-data="handleChangeData"
                            @close="close"
                        />

                        <upgrade-plan-item
                            v-if="isSelfUpgrade || !isLastOption"
                            v-bind:key="'pro-plan-annual'"
                            :type="'pro-plan-annual'"
                            :plan="currentPlan.id"
                            :period="'annual'"
                            :currentStep="currentStep"
                            :planList="planList"
                            :planCredit="getDefaultPlanCredit"
                            :defaultTarget="defaultTarget"
                            :currentTarget="target"
                            :defaultPlanName="getNameDefaultPlan"
                            @handle-change-data="handleChangeData"
                            @close="close"
                        />
                        <upgrade-plan-item
                            v-if="isSelfUpgrade || !isLastOption"
                            v-bind:key="'custom-annual'"
                            :type="'custom-annual'"
                            :plan="'custom-annual'"
                            :period="'annual'"
                            :currentStep="currentStep"
                            :planList="planList"
                            @handle-change-data="handleChangeData"
                            @close="close"
                        />
                    </template>
                </div>
            </div>

            <div
                v-if="planId !== 'free-plan-monthly' && planId !== 'free-plan-trial'"
                class="upgradePlan__bottomInfo"
            >
                <a href="#" @click="showEarlyDowngrade">Downgrade</a>
                to the Pay-As-You-Go plan.
            </div>
        </template>
    </div>
</template>

<script>
import { mapState } from 'pinia';
import stores from 'store/modules';
import Slider from '@vueform/slider';
import UpgradePlanItem from './UpgradePlanItem';
import ChangePlanNew from '../components/ChangePlanNew';
import EarlyUpgradeMixin from 'mixins/early-upgrade';
import OrganizationMixin from 'mixins/organization';

export default {
    emits: ['close', 'handleChangeData'],
    
    components: {
        Slider,
        UpgradePlanItem,
        ChangePlanNew,
    },

    mixins: [EarlyUpgradeMixin, OrganizationMixin],

    props: {
        form: { type: Object },
        useDowngrade: { type: Boolean, default: false },
        hasPlanDowngrade: { type: Boolean, default: false },
        showPreviewPlan: { type: Boolean, default: false },
        plan: { type: String, default: '', required: true },
        type: { type: String, default: '', required: true },
        price: { type: Number, default: 0, required: true },
        hasCard: { type: Boolean, default: false, required: true },
    },

    data() {
        return {
            value: 0,
            step: 1,
            min: 0,
            max: 6,
            buttons: [
                {
                    label: 'Monthly',
                    value: 'monthly',
                },
                {
                    label: 'Annually',
                    value: 'annual',
                },
            ],
            target: 'monthly',
            currentStep: 0,
            stepLine: [],
        };
    },

    computed: {
        modalTitle() {
            return `How many messages will you send per ${this.target === 'monthly' ? 'month' : 'year'}?`;
        },

        isSelfUpgrade() {
            return this.$route.name === 'settings.billing.overview.self_upgrade';
        },

        firstPlanCredits() {
            if (this.isSelfUpgrade) {
                const creditsMonthly = this.proPlanMonthlyTiersForSelfUpgrade[0].credits;
                const creditsAnnual = this.proPlanAnnualTiersForSelfUpgrade[0].credits;
                const credits = this.target === 'monthly' ? creditsMonthly : creditsAnnual;

                return `${credits / 1000}k`;
            }

            const creditsMonthly = this.proPlanMonthlyTiersForUsers[0].credits;
            const creditsAnnual = this.proPlanAnnualTiersForUsers[0].credits;
            const credits = this.target === 'monthly' ? creditsMonthly : creditsAnnual;

            if (credits < 1000) {
                return credits;
            } else {
                return `${credits / 1000}k`;
            }
        },

        lastPlanCredits() {
            if (this.isSelfUpgrade) {
                const creditsMonthly =
                    this.proPlanMonthlyTiersForSelfUpgrade[
                        this.proPlanMonthlyTiersForSelfUpgrade.length - 1
                    ].credits;
                const creditsAnnual =
                    this.proPlanAnnualTiersForSelfUpgrade[
                        this.proPlanAnnualTiersForSelfUpgrade.length - 1
                    ].credits;
                const credits = this.target === 'monthly' ? creditsMonthly : creditsAnnual;

                return `${credits / 1000}k`;
            }

            const creditsMonthly =
                this.proPlanMonthlyTiersForUsers[this.proPlanMonthlyTiersForUsers.length - 1]
                    .credits;
            const creditsAnnual =
                this.proPlanAnnualTiersForUsers[this.proPlanAnnualTiersForUsers.length - 1].credits;
            const credits = this.target === 'monthly' ? creditsMonthly : creditsAnnual;

            if (credits < 1000) {
                return credits;
            } else {
                return `${credits / 1000}k`;
            }
        },

        currentPlan() {
            return this.planList[this.currentStep];
        },

        planList() {
            if (this.target === 'monthly') {
                if (this.isSelfUpgrade) {
                    return this.proPlanMonthlyTiersForSelfUpgrade;
                }

                return this.proPlanMonthlyTiersForUsers;
            }

            if (this.isSelfUpgrade) {
                return this.proPlanAnnualTiersForSelfUpgrade;
            }

            return this.proPlanAnnualTiersForUsers;
        },

        getDefaultStep() {
            if (!this.checkOldBillingPlans) {
                return this.planList.findIndex((o) => o.id === this.getDefaultPlan.id);
            }
        },

        defaultTarget() {
            return this.target === this.getNameDefaultPlan;
        },

        isLastOption() {
            return this.currentStep === this.max;
        },

        ...mapState(stores.Subscriptions, ['subscription']),
        ...mapState(stores.BillingSources, ['billingPlans']),
    },

    methods: {
        init() {
            this.initSlider();
        },

        initSlider() {
            this.min = 0;
            this.max = this.planList.length - 1;
            this.stepLine = Array.from({ length: this.max }, () => 'line');

            if (!this.checkOldBillingPlans) {
                this.value = this.isCanceledOrTrial ? 0 : this.getDefaultStep;
            }
        },

        close() {
            this.$emit('close');
        },

        formatChange(value) {
            this.currentStep = Math.round(value);

            const credits = this.currentPlan.credits;

            if (credits < 1000) {
                return credits;
            } else {
                if (this.isLastOption) return `${credits / 1000}k+`;

                return `${credits / 1000}k`;
            }
        },

        toggleButtons({ value }) {
            this.target = value;
        },

        handleChangeData(data) {
            this.$emit('handleChangeData', data);
        },
    },

    mounted() {
        this.init();
    },
};
</script>
